/**
 * VÉRAULT TRAVELS — Trip Timeline Component
 * 
 * Displays an aggregated timeline of milestones derived from REAL system data:
 * - Planning milestones (lifecycle history)
 * - Payment milestones (AR/AP records)
 * - Booking milestones (hotel, flight, etc.)
 * - Deadline markers (trip start, planning deadline, payment due)
 * - Forms/Documents
 * 
 * All entries are derived from existing system data — no duplicated entries.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Clock, CheckCircle2, AlertTriangle, DollarSign, Plane, Hotel,
  Sparkles, Calendar, FileText, ChevronDown, ChevronUp, Loader2,
  MapPin, Globe
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  sparkle: Sparkles,
  clock: Clock,
  dollar: DollarSign,
  plane: Plane,
  hotel: Hotel,
  globe: Globe,
  clipboard: FileText,
  calendar: Calendar,
  heart: Sparkles,
  map: MapPin,
};

const STATUS_STYLES = {
  complete: { bg: 'bg-[#8DA399]/10', border: 'border-[#8DA399]', dot: 'bg-[#8DA399]', text: 'text-[#8DA399]' },
  pending: { bg: 'bg-[#C8A85A]/10', border: 'border-[#C8A85A]', dot: 'bg-[#C8A85A]', text: 'text-[#C8A85A]' },
  overdue: { bg: 'bg-red-50', border: 'border-red-400', dot: 'bg-red-500', text: 'text-red-600' },
};

const TYPE_LABELS = {
  planning: 'Planning',
  payment: 'Payment',
  booking: 'Booking',
  deadline: 'Deadline',
};

export function TripTimeline({ tripId }) {
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    if (tripId) fetchTimeline();
  }, [tripId]);

  const fetchTimeline = async () => {
    try {
      const res = await axios.get(`${API}/travels/trips/${tripId}/timeline`);
      setTimeline(res.data);
    } catch (e) {
      setTimeline(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="w-5 h-5 animate-spin text-[#C8A85A]" />
      </div>
    );
  }

  if (!timeline || !timeline.milestones?.length) {
    return (
      <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-6" data-testid="trip-timeline">
        <h3 className="font-medium text-[#1A1A1A] flex items-center gap-2 mb-2">
          <Clock className="w-5 h-5 text-[#C8A85A]" />
          Trip Timeline
        </h3>
        <p className="text-sm text-[#8C8C8C]">No milestones recorded yet</p>
      </div>
    );
  }

  const milestones = filterType === 'all'
    ? timeline.milestones
    : timeline.milestones.filter(m => m.type === filterType);

  const summary = timeline.summary;

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    try {
      const d = new Date(ts);
      if (isNaN(d.getTime())) return ts;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return ts;
    }
  };

  return (
    <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-6" data-testid="trip-timeline">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between"
      >
        <h3 className="font-medium text-[#1A1A1A] flex items-center gap-2">
          <Clock className="w-5 h-5 text-[#C8A85A]" />
          Trip Timeline
          <span className="text-xs text-[#8C8C8C] font-normal">
            {summary.total} milestones
          </span>
        </h3>
        <div className="flex items-center gap-3">
          {summary.overdue > 0 && (
            <span className="text-xs px-2 py-0.5 bg-red-50 text-red-600 rounded-full" data-testid="timeline-overdue-badge">
              {summary.overdue} overdue
            </span>
          )}
          {summary.pending > 0 && (
            <span className="text-xs px-2 py-0.5 bg-[#C8A85A]/10 text-[#C8A85A] rounded-full">
              {summary.pending} pending
            </span>
          )}
          <span className="text-xs px-2 py-0.5 bg-[#8DA399]/10 text-[#8DA399] rounded-full">
            {summary.complete} done
          </span>
          {expanded ? <ChevronUp className="w-4 h-4 text-[#8C8C8C]" /> : <ChevronDown className="w-4 h-4 text-[#8C8C8C]" />}
        </div>
      </button>

      {expanded && (
        <div className="mt-4 space-y-3">
          {/* Filter Tabs */}
          <div className="flex gap-1.5 flex-wrap">
            {['all', 'planning', 'payment', 'booking', 'deadline'].map(t => (
              <button
                key={t}
                onClick={() => setFilterType(t)}
                className={`px-2.5 py-1 text-xs rounded-lg transition-colors ${
                  filterType === t
                    ? 'bg-[#C8A85A] text-white'
                    : 'bg-[#F5F1EB] text-[#595959] hover:bg-[#EEE9E2]'
                }`}
                data-testid={`timeline-filter-${t}`}
              >
                {t === 'all' ? 'All' : TYPE_LABELS[t]}
              </button>
            ))}
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-[15px] top-0 bottom-0 w-px bg-[#E6E1DA]" />

            <div className="space-y-1">
              {milestones.map((m, idx) => {
                const style = STATUS_STYLES[m.status] || STATUS_STYLES.pending;
                const IconComp = ICON_MAP[m.icon] || Clock;
                return (
                  <div key={m.id || idx} className="flex items-start gap-3 relative" data-testid={`timeline-milestone-${m.id}`}>
                    {/* Dot */}
                    <div className={`relative z-10 w-[30px] h-[30px] rounded-full flex items-center justify-center flex-shrink-0 ${style.bg} border ${style.border}`}>
                      {m.status === 'complete' ? (
                        <CheckCircle2 className={`w-4 h-4 ${style.text}`} />
                      ) : m.status === 'overdue' ? (
                        <AlertTriangle className={`w-4 h-4 ${style.text}`} />
                      ) : (
                        <IconComp className={`w-4 h-4 ${style.text}`} />
                      )}
                    </div>
                    {/* Content */}
                    <div className="flex-1 min-w-0 pb-3">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${style.bg} ${style.text} uppercase tracking-wide font-medium`}>
                          {TYPE_LABELS[m.type] || m.type}
                        </span>
                        {m.timestamp && (
                          <span className="text-xs text-[#8C8C8C]">{formatTimestamp(m.timestamp)}</span>
                        )}
                      </div>
                      <p className="text-sm font-medium text-[#1A1A1A] mt-0.5">{m.label}</p>
                      {m.description && (
                        <p className="text-xs text-[#8C8C8C] mt-0.5">{m.description}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <p className="text-xs text-[#B0B0B0] pt-2 border-t border-[rgba(0,0,0,0.06)]">
            All milestones derived from system data (payments, bookings, lifecycle history)
          </p>
        </div>
      )}
    </div>
  );
}

export default TripTimeline;
